import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Container, Paper, TextField, Button, Typography,
    Box, Grid, Alert, Autocomplete, FormControl,
    InputLabel, Select, MenuItem, Dialog, DialogTitle,
    DialogContent, DialogActions, Radio, RadioGroup,
    FormControlLabel, FormLabel
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Event as EventIcon,
    Notifications as NotificationsIcon,
    Assignment as AssignmentIcon
} from '@mui/icons-material';
import api from '../services/api';
import { isAdmin } from '../utils/auth';

function PrazoForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const [error, setError] = useState('');
    const [processos, setProcessos] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [partesExternas, setPartesExternas] = useState([]);
    const [varaList, setVaraList] = useState([]);
    const [openReenviarDialog, setOpenReenviarDialog] = useState(false);
    const [destinatarioReenviar, setDestinatarioReenviar] = useState('todos');
    const [usuarioSelecionadoReenviar, setUsuarioSelecionadoReenviar] = useState('');
    const [reenviando, setReenviando] = useState(false);
    const [formData, setFormData] = useState({
        processo_id: '',
        data_vencimento: '',
        prioridade: 'NORMAL',
        status: 'PENDENTE',
        responsavel_interno_id: '',
        responsavel_externo_id: '',
        observacoes: '',
        instrucoes: '',
        vara: ''
    });

    useEffect(() => {
        loadProcessos();
        loadUsuarios();
        loadPartesExternas();
        loadVaraList();
        if (id) {
            loadPrazo();
        } else {
            // Se não é edição, verifica se tem processo_id na URL
            const searchParams = new URLSearchParams(location.search);
            const processoId = searchParams.get('processo_id');
            if (processoId) {
                setFormData(prev => ({ ...prev, processo_id: processoId }));
            }
        }
    }, [id, location.search]);

    const loadProcessos = async () => {
        try {
            const response = await api.get('/processos?limit=1000');
            setProcessos(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar processos:', error);
        }
    };


    const loadUsuarios = async () => {
        try {
            const response = await api.get('/usuarios/lista');
            setUsuarios(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar usuários:', error);
        }
    };

    const loadPartesExternas = async () => {
        try {
            const response = await api.get('/parte-externa?ativo=true');
            setPartesExternas(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar partes externas:', error);
        }
    };

    const loadVaraList = async () => {
        try {
            const response = await api.get('/vara-lista?ativo=true');
            setVaraList(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar lista de varas:', error);
        }
    };

    // Função auxiliar para encontrar ou criar valor para Autocomplete
    const getValueForAutocomplete = (list, value, fieldName = 'valor') => {
        if (!value) return null;
        // Primeiro tenta encontrar na lista
        const found = list.find(item => item[fieldName] === value);
        if (found) return found;
        // Se não encontrou, cria um objeto temporário com o valor do banco (preserva dados da migração)
        return { [fieldName]: value, id: null, isCustom: true };
    };

    const loadPrazo = async () => {
        try {
            const response = await api.get(`/prazos/${id}`);
            const prazo = response.data;
            
            // Verifica se é processo nulo
            const isProcessoNulo = prazo.numero_processo && (
                prazo.numero_processo.includes('99999999999') || 
                prazo.numero_processo.includes('SEM PROCESSO') ||
                prazo.numero_processo.includes('NULO')
            );
            
            // Para processo nulo, concatena observacoes + motivo_finalizacao
            let observacoesValue = prazo.observacoes || '';
            if (isProcessoNulo && prazo.motivo_finalizacao) {
                observacoesValue = prazo.observacoes 
                    ? `${prazo.observacoes} - ${prazo.motivo_finalizacao}`
                    : prazo.motivo_finalizacao;
            }
            
            setFormData({
                processo_id: prazo.processo_id || '',
                data_vencimento: prazo.data_vencimento ? prazo.data_vencimento.split('T')[0] : '',
                prioridade: prazo.prioridade || 'NORMAL',
                status: prazo.status || 'PENDENTE',
                responsavel_interno_id: prazo.responsavel_interno_id || '',
                responsavel_externo_id: prazo.responsavel_externo_id || '',
                observacoes: observacoesValue,
                instrucoes: prazo.instrucoes || '',
                vara: prazo.vara || ''
            });
        } catch (error) {
            console.error('Erro ao carregar prazo:', error);
            
            // Se o prazo não foi encontrado (404) ou foi deletado, redireciona para a lista
            if (error.response?.status === 404) {
                alert('Prazo não encontrado. Este prazo pode ter sido deletado.');
                navigate('/prazos');
                return;
            }
            
            setError('Erro ao carregar dados do prazo');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.processo_id || !formData.data_vencimento) {
            setError('Processo e data de vencimento são obrigatórios');
            return;
        }

        try {
            if (id) {
                await api.put(`/prazos/${id}`, formData);
            } else {
                await api.post('/prazos', formData);
            }
            navigate('/prazos');
        } catch (error) {
            console.error('Erro ao salvar prazo:', error);
            setError(error.response?.data?.error || 'Erro ao salvar prazo');
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleAbrirReenviarDialog = () => {
        if (!id) return;
        setOpenReenviarDialog(true);
        setDestinatarioReenviar('todos');
        setUsuarioSelecionadoReenviar('');
    };

    const handleReenviarNotificacao = async () => {
        if (!id) return;
        
        try {
            setReenviando(true);
            const payload = destinatarioReenviar === 'todos' 
                ? {} 
                : { usuario_id: parseInt(usuarioSelecionadoReenviar) };
            
            const response = await api.post(`/notificacoes/reenviar/${id}`, payload);
            alert(`✅ ${response.data.message}`);
            setOpenReenviarDialog(false);
            setDestinatarioReenviar('todos');
            setUsuarioSelecionadoReenviar('');
        } catch (error) {
            console.error('Erro ao reenviar notificação:', error);
            alert('Erro ao reenviar notificação: ' + (error.response?.data?.error || error.message));
        } finally {
            setReenviando(false);
        }
    };

    const processoSelecionado = processos.find(p => p.id === parseInt(formData.processo_id));

    return (
        <Container maxWidth="lg">
            {/* Header destacado */}
            <Box 
                sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    boxShadow: 2,
                    mt: 2
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <EventIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    {id ? 'Editar Prazo' : 'Novo Prazo'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    {id ? 'Atualize as informações do prazo' : 'Cadastre um novo prazo judicial'}
                </Typography>
            </Box>

            <Paper sx={{ p: 4, mt: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={processos}
                                getOptionLabel={(option) => `${option.numero_processo || ''} - ${option.cliente_nome || ''}`}
                                value={processoSelecionado || null}
                                onChange={(e, newValue) => {
                                    setFormData({ ...formData, processo_id: newValue ? newValue.id : '' });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Processo *"
                                        required
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={varaList}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.valor || '';
                                }}
                                value={getValueForAutocomplete(varaList, formData.vara)}
                                onChange={(e, newValue) => {
                                    setFormData({ 
                                        ...formData, 
                                        vara: newValue ? (typeof newValue === 'string' ? newValue : newValue.valor) : '' 
                                    });
                                }}
                                freeSolo
                                renderInput={(params) => (
                                    <TextField {...params} label="Vara" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                label="Data de Vencimento *"
                                name="data_vencimento"
                                type="date"
                                value={formData.data_vencimento}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Prioridade</InputLabel>
                                <Select
                                    value={formData.prioridade}
                                    onChange={handleChange}
                                    name="prioridade"
                                    label="Prioridade"
                                >
                                    <MenuItem value="BAIXA">Baixa</MenuItem>
                                    <MenuItem value="NORMAL">Normal</MenuItem>
                                    <MenuItem value="ALTA">Alta</MenuItem>
                                    <MenuItem value="URGENTE">Urgente</MenuItem>
                                    <MenuItem value="FATAL">Fatal</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={formData.status}
                                    onChange={handleChange}
                                    name="status"
                                    label="Status"
                                >
                                    <MenuItem value="PENDENTE">Pendente</MenuItem>
                                    <MenuItem value="CONCLUIDO">Concluído</MenuItem>
                                    <MenuItem value="CANCELADO">Cancelado</MenuItem>
                                    <MenuItem value="VENCIDO">Vencido</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Responsável Interno</InputLabel>
                                <Select
                                    value={formData.responsavel_interno_id}
                                    onChange={handleChange}
                                    name="responsavel_interno_id"
                                    label="Responsável Interno"
                                >
                                    <MenuItem value="">Nenhum</MenuItem>
                                    {usuarios.map((u) => (
                                        <MenuItem key={u.id} value={u.id}>
                                            {u.nome} ({u.username})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={partesExternas}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.nome_completo || '';
                                }}
                                value={partesExternas.find(pe => pe.id === parseInt(formData.responsavel_externo_id)) || null}
                                onChange={(e, newValue) => {
                                    setFormData({ 
                                        ...formData, 
                                        responsavel_externo_id: newValue ? newValue.id : '' 
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Responsável Externo" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Observações"
                                name="observacoes"
                                value={formData.observacoes}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Instruções (o que deve ser feito)"
                                name="instrucoes"
                                value={formData.instrucoes}
                                onChange={handleChange}
                                placeholder="Descreva as instruções sobre o que deve ser feito neste prazo..."
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        {id && isAdmin() && (
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<NotificationsIcon />}
                                onClick={handleAbrirReenviarDialog}
                                sx={{ mr: 'auto' }}
                            >
                                Reenviar Notificação
                            </Button>
                        )}
                        {formData.processo_id && (
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<AssignmentIcon />}
                                onClick={() => navigate(`/processos/${formData.processo_id}/andamentos`)}
                                sx={id && isAdmin() ? {} : { mr: 'auto' }}
                            >
                                Gerenciar Andamentos
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/prazos')}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                        >
                            {id ? 'Salvar Alterações' : 'Criar Prazo'}
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* Dialog para Reenviar Notificação */}
            <Dialog 
                open={openReenviarDialog} 
                onClose={() => setOpenReenviarDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reenviar Notificação</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">Enviar notificação para:</FormLabel>
                            <RadioGroup
                                value={destinatarioReenviar}
                                onChange={(e) => setDestinatarioReenviar(e.target.value)}
                            >
                                <FormControlLabel 
                                    value="todos" 
                                    control={<Radio />} 
                                    label="Todos os usuários relacionados ao prazo" 
                                />
                                <FormControlLabel 
                                    value="especifico" 
                                    control={<Radio />} 
                                    label="Usuário específico" 
                                />
                            </RadioGroup>
                        </FormControl>

                        {destinatarioReenviar === 'especifico' && (
                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <InputLabel>Selecione o usuário</InputLabel>
                                <Select
                                    value={usuarioSelecionadoReenviar}
                                    onChange={(e) => setUsuarioSelecionadoReenviar(e.target.value)}
                                    label="Selecione o usuário"
                                >
                                    {usuarios.map((usuario) => (
                                        <MenuItem key={usuario.id} value={usuario.id}>
                                            {usuario.nome || usuario.username}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <Alert severity="info" sx={{ mt: 2 }}>
                            A notificação será criada com base no status e data de vencimento atual do prazo.
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenReenviarDialog(false)}>
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleReenviarNotificacao}
                        variant="contained"
                        disabled={reenviando || (destinatarioReenviar === 'especifico' && !usuarioSelecionadoReenviar)}
                    >
                        {reenviando ? 'Enviando...' : 'Reenviar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default PrazoForm;

